import type { LockCalculatorFormValues } from "./form";
import { getStructuralVisibility, hasRealLoadValue } from "./structuralVisibility";

const LAST_STEP = 3;

const isFilled = (value: unknown): boolean => value != null && value !== "" && !Number.isNaN(value);

/**
 * Index of the last wizard step the user may open: a step is reachable only
 * once every step before it is complete. Conditional fields count only while
 * their form shows them (same conditions as EnvironmentForm/StructureStep).
 * Connectors is the last step, so it gates nothing.
 *
 * Left optional on purpose: joist β angle and connector top distance (the
 * latter defaults to the lock module's c_min).
 */
export const getLastReachableStep = (values: LockCalculatorFormValues, isEC5: boolean): number => {
  const { environment, loads, headerMember, joistMember } = values;
  const visibility = getStructuralVisibility(loads);
  const completeSteps = [
    isFilled(environment.standardId) &&
      isFilled(environment.serviceClassId) &&
      isFilled(environment.loadDurationClassId) &&
      (isEC5 || isFilled(environment.continuousControl)),
    // Every load is optional on its own, but a calculation without any load has nothing to verify.
    Object.values(loads).some(hasRealLoadValue),
    [
      headerMember.materialGradeId,
      headerMember.width,
      headerMember.height,
      headerMember.orientation,
      joistMember.materialGradeId,
      joistMember.width,
      joistMember.height,
      joistMember.alphaDeg,
      ...(hasRealLoadValue(loads.lateral) ? [values.lateralResistanceConfig] : []),
      ...(visibility.showUpliftResistanceConfig ? [values.upliftResistanceConfig] : []),
    ].every(isFilled),
  ];
  const firstIncompleteStep = completeSteps.indexOf(false);
  return firstIncompleteStep === -1 ? LAST_STEP : firstIncompleteStep;
};
